import { useState } from 'react';
import { generateClassName, getColorScheme } from '@klv/shared/utils/functions';
import AccordionMolecule from '@klv/molecules/accordion';
import { ModulesGrid } from '@klv/utils/grid';
import Eyebrow from '@klv/atoms/eyebrow';
import MixedMedia from '@klv/molecules/mixed-media';
import MixedMedias from '@klv/molecules/mixed-medias';

import * as s from './styles/accordion.module.css';

import type { IAccordionRow } from '@klv/molecules/accordion/types';
import type { IMixedMedia } from '@klv/molecules/mixed-media/types';
import type { IAccordion } from './types';
import type { TBaseProps } from '@klv/shared/types';

const getMediaData = (rows: IAccordionRow[]): IMixedMedia[] => rows.map((row) => row?.mixedMedia || { mediaType: 'image' });

const mediaDataIsValid = (rows: IAccordionRow[]) => {
  const validRows = rows.filter((row) => (row?.mixedMedia?.mediaType === 'image' && row?.mixedMedia?.image !== null) || row?.mixedMedia?.video?.videoUrl !== null);
  return validRows.length === rows.length;
};

const Accordion = ({
  accordionItems,
  backgroundColor,
  eyebrow = '',
  headline = '',
  mediaOverride = false,
  isFaqsSection = false,
  mediaAlignment,
  mixedMedia,
  showMedia,
  subhead = '',
  academyVariant,
  ...props
}: IAccordion & TBaseProps<'div'>) => {
  const { className, ...restOfProps } = props;
  const [expandedRow, setExpandedRow] = useState(0);
  const showMediaInline = mediaAlignment === 'inline';
  const shouldShowMedia = !showMediaInline && (mediaOverride || (showMedia && mediaDataIsValid(accordionItems)));

  const globalPaddingClasses = ['defaultPaddingTop', 'defaultPaddingBottom'];
  const mediaAlignmentClass = mediaAlignment === 'right' ? s.mediaAlignRight : s.mediaAlignLeft;

  return (
    <div
      {...restOfProps}
      className={generateClassName([s.accordion, ...(academyVariant ? s.academyMargin : globalPaddingClasses), className])}
      data-color-scheme={getColorScheme(backgroundColor)}
    >
      <ModulesGrid className={s.grid}>
        <div className={generateClassName([s.gridText, !shouldShowMedia ? s.fullWidth : mediaAlignmentClass])}>
          {eyebrow && <Eyebrow textColor={backgroundColor === 'dark' ? 'white' : 'black'} className={s.eyebrow} content={eyebrow} />}
          {headline && <h2 className={generateClassName([s.headline, 'h3'])}>{headline}</h2>}
          {subhead && <div className={generateClassName([s.subhead, academyVariant ? 'h6' : 'p2'])} dangerouslySetInnerHTML={{ __html: subhead }} />}
          <AccordionMolecule
            expandedRow={expandedRow} 
            isFaqsSection={isFaqsSection}
            setExpandedRow={setExpandedRow}
            rowHeadlineTag={headline ? 'h3' : 'h2'}
            rows={accordionItems}
            variant={backgroundColor}
            showMediaInline={showMediaInline}
          />
        </div>
        {shouldShowMedia ? (
          <div className={generateClassName([s.gridMedia, mediaAlignmentClass, mediaOverride && s.mediaOverride])}>
            {mediaOverride && mixedMedia ? (
              <MixedMedia mixedMedia={mixedMedia} />
            ) : (
              <MixedMedias
                currentIndex={expandedRow}
                mixedMediaData={getMediaData(accordionItems)}
              />
            )}
          </div>
        ) : null}
      </ModulesGrid>
    </div>
  );
};

export default Accordion;
